"use client";

import { type AudioPlayer, createAudioPlayer } from "@bandwith/platform";
import {
  Pause,
  Play,
  Repeat,
  Repeat1,
  Shuffle,
  SkipBack,
  SkipForward,
  X,
} from "lucide-react";
import { type ReactNode, useCallback, useEffect, useRef } from "react";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { useShallow } from "zustand/react/shallow";
import {
  FloatingAudioControl,
  PrimaryButton,
  ProgressBar,
} from "./FloatingAudioControl";
import { formatDuration } from "./formatTime";

export type GlobalTrack = {
  id: string;
  filename: string;
  signedAudioUrl: string;
  songId: string;
  songTitle: string;
  authorName: string | null;
};

function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const temp = shuffled[i];
    const swapVal = shuffled[j];
    if (temp !== undefined && swapVal !== undefined) {
      shuffled[i] = swapVal;
      shuffled[j] = temp;
    }
  }
  return shuffled;
}

/**
 * How playback behaves when a track finishes.
 * - "off": stop at the end of the queue
 * - "all": wrap to the start of the queue
 * - "one": replay the current track forever
 */
type RepeatMode = "off" | "all" | "one";

// Order the single control cycles through, matching the convention used by
// every music player: off -> repeat queue -> repeat track -> off.
const REPEAT_CYCLE = ["off", "all", "one"] as const satisfies RepeatMode[];

function nextRepeatMode(mode: RepeatMode): RepeatMode {
  const index = REPEAT_CYCLE.indexOf(mode);
  return REPEAT_CYCLE[(index + 1) % REPEAT_CYCLE.length] ?? "off";
}

const REPEAT_LABELS: Record<RepeatMode, string> = {
  off: "Repeat: Off",
  all: "Repeat: All",
  one: "Repeat: One",
};

type PlayerState = {
  tracks: GlobalTrack[];
  originalTracks: GlobalTrack[];
  currentTrackIndex: number | null;
  isPlaying: boolean;
  isShuffled: boolean;
  repeatMode: RepeatMode;
  isVisible: boolean;
  currentTime: number;
  duration: number;
  pendingSeekTime: number | null;
  // Bumped when the same track index must reload (queue wrap on a single-track
  // queue, or a repeat-one replay), since the load effect only fires on
  // index/id changes
  playbackNonce: number;
};

type PlayerActions = {
  play: (tracks: GlobalTrack[], startIndex?: number, shuffle?: boolean) => void;
  pause: () => void;
  resume: () => void;
  /** Manual skip forward. Always leaves the current track, whatever the repeat mode. */
  next: () => void;
  /** The current track played out on its own. Honours repeat mode. */
  trackEnded: () => void;
  previous: () => void;
  toggleShuffle: () => void;
  cycleRepeatMode: () => void;
  close: () => void;
  setIsPlaying: (isPlaying: boolean) => void;
  setCurrentTime: (time: number) => void;
  setDuration: (duration: number) => void;
  seekTo: (time: number) => void;
};

const STORAGE_KEY = "bandwith-player-state";

// Bumped when the persisted shape changes; see migratePersistedState
const STORAGE_VERSION = 1;

const initialState: PlayerState = {
  tracks: [],
  originalTracks: [],
  currentTrackIndex: null,
  isPlaying: false,
  isShuffled: false,
  repeatMode: "off",
  isVisible: false,
  currentTime: 0,
  duration: 0,
  pendingSeekTime: null,
  playbackNonce: 0,
};

// Only the state needed to restore playback position after auth redirects.
// Don't persist isPlaying (browsers block auto-play) or pendingSeekTime
// (transient). Track audio URLs are stable server redirects, so persisted
// state never goes stale.
type PersistedPlayerState = Pick<
  PlayerState,
  | "tracks"
  | "originalTracks"
  | "currentTrackIndex"
  | "isShuffled"
  | "repeatMode"
  | "isVisible"
  | "currentTime"
  | "duration"
>;

function persistedSlice(state: PlayerState): PersistedPlayerState {
  return {
    tracks: state.tracks,
    originalTracks: state.originalTracks,
    currentTrackIndex: state.currentTrackIndex,
    isShuffled: state.isShuffled,
    repeatMode: state.repeatMode,
    isVisible: state.isVisible,
    currentTime: state.currentTime,
    duration: state.duration,
  };
}

/**
 * Version 0 stored a boolean `isLooping`; version 1 stores `repeatMode`.
 * A stored `true` meant "repeat the queue", so it upgrades to "all" rather
 * than silently dropping back to "off".
 */
function migratePersistedState(
  persisted: unknown,
  version: number,
): PersistedPlayerState {
  const { isLooping, ...rest } = (persisted ?? {}) as Partial<
    PersistedPlayerState & { isLooping: boolean }
  >;
  return {
    ...persistedSlice(initialState),
    ...rest,
    repeatMode:
      version < STORAGE_VERSION
        ? isLooping
          ? "all"
          : "off"
        : (rest.repeatMode ?? "off"),
  };
}

type PlayerStore = PlayerState & PlayerActions;

const usePlayerStore = create<PlayerStore>()(
  persist(
    (set, get) => ({
      // State
      ...initialState,

      // Actions
      play: (tracks, startIndex = 0, shuffle = false) => {
        const playTracks = shuffle ? shuffleArray(tracks) : tracks;
        const playIndex = shuffle ? 0 : startIndex;
        set({
          tracks: playTracks,
          originalTracks: tracks,
          currentTrackIndex: playIndex,
          isPlaying: true,
          isShuffled: shuffle,
          isVisible: true,
        });
      },

      pause: () => set({ isPlaying: false }),

      resume: () => set({ isPlaying: true }),

      // Manual skip forward. Repeat-one repeats a track you let play out, not
      // one you deliberately skipped, so this always leaves the current track.
      // Any repeat mode still means "the queue doesn't end", so both wrap.
      next: () => {
        const { currentTrackIndex, tracks, repeatMode, playbackNonce } = get();
        if (currentTrackIndex === null) return;
        const nextIndex = currentTrackIndex + 1;
        if (nextIndex < tracks.length) {
          set({ currentTrackIndex: nextIndex, isPlaying: true });
        } else if (repeatMode !== "off") {
          set({
            currentTrackIndex: 0,
            isPlaying: true,
            playbackNonce: playbackNonce + 1,
          });
        } else {
          set({ isPlaying: false, currentTrackIndex: 0 });
        }
      },

      trackEnded: () => {
        const { currentTrackIndex, repeatMode, playbackNonce, next } = get();
        if (currentTrackIndex === null) return;
        if (repeatMode === "one") {
          // Same index, so only the nonce can retrigger the load effect
          set({ isPlaying: true, playbackNonce: playbackNonce + 1 });
          return;
        }
        next();
      },

      previous: () => {
        const { currentTrackIndex } = get();
        if (currentTrackIndex === null) return;
        if (currentTrackIndex === 0) return;
        set({ currentTrackIndex: currentTrackIndex - 1, isPlaying: true });
      },

      toggleShuffle: () => {
        const { isShuffled, tracks, originalTracks, currentTrackIndex } = get();
        const currentTrack =
          currentTrackIndex !== null ? tracks[currentTrackIndex] : null;

        if (isShuffled) {
          const newIndex = currentTrack
            ? originalTracks.findIndex((t) => t.id === currentTrack.id)
            : 0;
          set({
            tracks: originalTracks,
            currentTrackIndex: newIndex >= 0 ? newIndex : 0,
            isShuffled: false,
          });
        } else {
          const otherTracks = tracks.filter((t) => t.id !== currentTrack?.id);
          const newTracks = currentTrack
            ? [currentTrack, ...shuffleArray(otherTracks)]
            : shuffleArray(otherTracks);
          set({
            tracks: newTracks,
            currentTrackIndex: 0,
            isShuffled: true,
          });
        }
      },

      cycleRepeatMode: () =>
        set((state) => ({ repeatMode: nextRepeatMode(state.repeatMode) })),

      close: () => set({ ...initialState }),

      setIsPlaying: (isPlaying) => set({ isPlaying }),

      setCurrentTime: (time) => set({ currentTime: time }),

      setDuration: (duration) => set({ duration }),

      seekTo: (time) => set({ pendingSeekTime: time }),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => sessionStorage),
      version: STORAGE_VERSION,
      // Wrapped rather than passed by reference: a bare function reference
      // would make TypeScript infer the store type from its parameter and lose
      // the action types
      partialize: (state) => persistedSlice(state),
      migrate: migratePersistedState,
    },
  ),
);

const selectAll = (state: PlayerStore) => state;

/**
 * Subscribe to the global player. Pass a selector to re-render only when the
 * picked fields change (compared shallowly); with no selector the component
 * re-renders on every store update, including each `timeupdate` tick.
 */
export function useGlobalPlayer(): PlayerStore;
export function useGlobalPlayer<T>(selector: (state: PlayerStore) => T): T;
export function useGlobalPlayer<T>(
  selector: (state: PlayerStore) => T | PlayerStore = selectAll,
) {
  return usePlayerStore(useShallow(selector));
}

// Read the store outside React (event handlers needing the latest value without
// subscribing, and tests)
export const getPlayerState = () => usePlayerStore.getState();
export const setPlayerState = (state: Partial<PlayerState>) =>
  usePlayerStore.setState(state);

// Re-read (and migrate) persisted state from storage. Exported so tests can
// exercise the stored-shape migration without reloading the page.
export const rehydratePlayerStore = () => usePlayerStore.persist.rehydrate();

// Reset store to initial state (for testing)
export function resetPlayerStore() {
  usePlayerStore.setState(initialState);
  // Also clear persisted storage to ensure clean state between tests
  if (typeof sessionStorage !== "undefined") {
    sessionStorage.removeItem(STORAGE_KEY);
  }
}

// Provider component that renders audio element and player UI
export function GlobalPlayerProvider({ children }: { children: ReactNode }) {
  return (
    <>
      {children}
      <GlobalPlayerAudio />
      <GlobalPlayerUI />
    </>
  );
}

// Audio controller using platform AudioPlayer abstraction
function GlobalPlayerAudio() {
  const playerRef = useRef<AudioPlayer | null>(null);
  // The audio element fires both `pause` and `ended` at natural track end,
  // so the advance-on-end handler below would otherwise run twice per track
  const advanceHandledRef = useRef(false);
  const tracks = usePlayerStore((s) => s.tracks);
  const currentTrackIndex = usePlayerStore((s) => s.currentTrackIndex);
  const isPlaying = usePlayerStore((s) => s.isPlaying);
  const playbackNonce = usePlayerStore((s) => s.playbackNonce);
  const pendingSeekTime = usePlayerStore((s) => s.pendingSeekTime);
  const trackEnded = usePlayerStore((s) => s.trackEnded);
  const setIsPlaying = usePlayerStore((s) => s.setIsPlaying);
  const setCurrentTime = usePlayerStore((s) => s.setCurrentTime);
  const setDuration = usePlayerStore((s) => s.setDuration);
  const close = usePlayerStore((s) => s.close);
  const isVisible = usePlayerStore((s) => s.isVisible);

  const currentTrack =
    currentTrackIndex !== null ? tracks[currentTrackIndex] : null;

  // Initialize player and set up subscriptions
  useEffect(() => {
    const player = createAudioPlayer();
    playerRef.current = player;

    const unsubState = player.onStateChange((state) => {
      setIsPlaying(state.isPlaying);
      setDuration(state.duration);

      // When playback stops naturally (position equals duration), let the
      // store decide whether to repeat the track or move on
      if (
        !state.isPlaying &&
        state.position >= state.duration - 0.1 &&
        state.duration > 0 &&
        !advanceHandledRef.current
      ) {
        advanceHandledRef.current = true;
        trackEnded();
      }
    });

    const unsubTime = player.onTimeUpdate(setCurrentTime);

    const unsubError = player.onError((error) => {
      console.error("Audio playback error - closing player:", error.message);
      close();
    });

    return () => {
      unsubState();
      unsubTime();
      unsubError();
      player.unload();
    };
  }, [setIsPlaying, setDuration, setCurrentTime, close, trackEnded]);

  // Load track when it changes (playbackNonce covers same-index reloads:
  // a repeat-one replay, or a single-track queue wrapping in repeat-all)
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally only re-run on track id/nonce changes
  useEffect(() => {
    const player = playerRef.current;
    if (!player || !currentTrack) return;

    advanceHandledRef.current = false;
    player
      .load(currentTrack.signedAudioUrl)
      .then(() => {
        if (isPlaying) {
          player.play().catch(() => setIsPlaying(false));
        }
      })
      .catch(() => {
        console.error("Failed to load track");
        close();
      });
  }, [currentTrackIndex, currentTrack?.id, playbackNonce, setIsPlaying, close]);

  // Play/pause when isPlaying changes
  useEffect(() => {
    const player = playerRef.current;
    if (!player || !currentTrack) return;

    if (isPlaying) {
      player.play().catch(() => setIsPlaying(false));
    } else {
      player.pause();
    }
  }, [isPlaying, currentTrack, setIsPlaying]);

  // Handle previous button - restart track if > 3 seconds in, otherwise go to previous
  const handlePrevious = useCallback(() => {
    const player = playerRef.current;
    const { currentTrackIndex } = usePlayerStore.getState();
    const canGoBack = currentTrackIndex !== null && currentTrackIndex > 0;
    const shouldRestart = !canGoBack || (player && player.getCurrentTime() > 3);

    if (shouldRestart) {
      player?.seekTo(0);
      player?.play().catch(() => {});
    } else {
      usePlayerStore.setState({
        currentTrackIndex: currentTrackIndex - 1,
        isPlaying: true,
      });
    }
  }, []);

  // Replace the store's previous action with our audio-aware version
  useEffect(() => {
    usePlayerStore.setState({ previous: handlePrevious });
  }, [handlePrevious]);

  // Stop playback when player is closed
  useEffect(() => {
    if (!isVisible) {
      const player = playerRef.current;
      if (player) {
        player.pause();
        player.unload();
      }
    }
  }, [isVisible]);

  // Handle seek requests from the UI
  useEffect(() => {
    const player = playerRef.current;
    if (!player || pendingSeekTime === null) return;

    player.seekTo(pendingSeekTime);
    usePlayerStore.setState({ pendingSeekTime: null });
  }, [pendingSeekTime]);

  // No DOM element needed - player is imperative
  return null;
}

// Play/pause button using shared PrimaryButton
function PlayPauseButton({
  isPlaying,
  onToggle,
  size = "normal",
}: {
  isPlaying: boolean;
  onToggle: () => void;
  size?: "normal" | "compact";
}) {
  return (
    <PrimaryButton
      icon={
        isPlaying ? (
          <Pause className="h-5 w-5" aria-hidden="true" />
        ) : (
          <Play className="h-5 w-5" aria-hidden="true" />
        )
      }
      onClick={onToggle}
      ariaLabel={isPlaying ? "Pause" : "Play"}
      size={size}
      variant="amber"
    />
  );
}

// Player UI component - Floating corner player using shared FloatingAudioControl
function GlobalPlayerUI() {
  const tracks = usePlayerStore((s) => s.tracks);
  const currentTrackIndex = usePlayerStore((s) => s.currentTrackIndex);
  const isPlaying = usePlayerStore((s) => s.isPlaying);
  const isShuffled = usePlayerStore((s) => s.isShuffled);
  const repeatMode = usePlayerStore((s) => s.repeatMode);
  const isVisible = usePlayerStore((s) => s.isVisible);
  const currentTime = usePlayerStore((s) => s.currentTime);
  const duration = usePlayerStore((s) => s.duration);
  const pause = usePlayerStore((s) => s.pause);
  const resume = usePlayerStore((s) => s.resume);
  const next = usePlayerStore((s) => s.next);
  const previous = usePlayerStore((s) => s.previous);
  const toggleShuffle = usePlayerStore((s) => s.toggleShuffle);
  const cycleRepeatMode = usePlayerStore((s) => s.cycleRepeatMode);
  const close = usePlayerStore((s) => s.close);
  const seekTo = usePlayerStore((s) => s.seekTo);

  const currentTrack =
    currentTrackIndex !== null ? tracks[currentTrackIndex] : null;

  if (!currentTrack) {
    return null;
  }

  return (
    <FloatingAudioControl
      isVisible={isVisible}
      primaryButton={
        <PlayPauseButton
          isPlaying={isPlaying}
          onToggle={isPlaying ? pause : resume}
        />
      }
      title={currentTrack.songTitle}
      timeDisplay={`${formatDuration(currentTime)} / ${formatDuration(duration)}`}
      meter={
        <ProgressBar
          currentTime={currentTime}
          duration={duration}
          onSeek={seekTo}
        />
      }
      onClose={close}
      expandLabel="Expand player"
      collapseLabel="Collapse player"
      closeLabel="Close player"
      testId="global-player"
      expandedContent={
        <>
          {/* Filename and author */}
          <p className="mb-2 truncate font-mono text-xs text-text-secondary">
            {currentTrack.filename}
          </p>
          {currentTrack.authorName && (
            <p className="mb-2 truncate text-xs text-text-muted">
              by {currentTrack.authorName}
            </p>
          )}

          {/* Additional controls */}
          <div className="flex items-center justify-center gap-1">
            <button
              type="button"
              onClick={previous}
              className="rounded p-2 text-text-secondary transition-colors hover:bg-bg-tertiary hover:text-text-primary"
              aria-label="Previous track"
            >
              <SkipBack className="h-5 w-5" aria-hidden="true" />
            </button>

            <button
              type="button"
              onClick={next}
              className="rounded p-2 text-text-secondary transition-colors hover:bg-bg-tertiary hover:text-text-primary"
              aria-label="Next track"
            >
              <SkipForward className="h-5 w-5" aria-hidden="true" />
            </button>

            <button
              type="button"
              onClick={toggleShuffle}
              className={`rounded p-2 transition-colors ${
                isShuffled
                  ? "text-amber hover:text-amber-warm"
                  : "text-text-secondary hover:bg-bg-tertiary hover:text-text-primary"
              }`}
              aria-label={isShuffled ? "Disable shuffle" : "Enable shuffle"}
              aria-pressed={isShuffled}
            >
              <Shuffle className="h-5 w-5" aria-hidden="true" />
            </button>

            {/*
              Three states, so no aria-pressed: it is a boolean affordance and
              its only third value, "mixed", means partially pressed — wrong for
              "repeat one". Instead the accessible name carries the current
              state, which screen readers re-announce as the button cycles.
            */}
            <button
              type="button"
              onClick={cycleRepeatMode}
              className={`rounded p-2 transition-colors ${
                repeatMode === "off"
                  ? "text-text-secondary hover:bg-bg-tertiary hover:text-text-primary"
                  : "text-amber hover:text-amber-warm"
              }`}
              aria-label={REPEAT_LABELS[repeatMode]}
              title={REPEAT_LABELS[repeatMode]}
            >
              {repeatMode === "one" ? (
                <Repeat1 className="h-5 w-5" aria-hidden="true" />
              ) : (
                <Repeat className="h-5 w-5" aria-hidden="true" />
              )}
            </button>

            <button
              type="button"
              onClick={close}
              className="rounded p-2 text-text-secondary transition-colors hover:bg-bg-tertiary hover:text-text-primary"
              aria-label="Close player"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </>
      }
    />
  );
}
